package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/channel")
@Composable
fun ChannelPage() {
    BlogLayout(
        PageProperties(
            "Coroutines Channel",
            "Coroutines execute long-running tasks without blocking the main thread or freezing the UI."
        ),
        BlogProperties("Coroutines", "Coroutines Channel")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Channel is a coroutine-based primitive that allows sending one-time events or data between coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Are a hot stream of values. Support any numbers of senders and receivers. Every value send to the channel is received only once.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channels facilitate asynchronous communication between coroutines, offering a unidirectional conduit for sending and receiving values. They provide explicit control over data transfer and can be closed when no longer needed for efficient resource management.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutine channels are a powerful mechanism in Kotlin coroutines for facilitating the communication of data between coroutines in a producer-consumer pattern.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hey are especially useful in scenarios where you need to pass streams of data between coroutines while ensuring that the communication is safe and non-blocking.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They have 2 suspending function send and receive.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "receive is suspended if there are no elements in the channel and will wait for an element to be available to resume.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "send is suspended if the channel reaches its capacity (see below for the channel capacity).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can also use the non suspending version trySend and tryReceive which return a ChannelResult (tells us if the operation was successful or not).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If there are several receivers subscribed at the same time, the elements will be fairly distributed between the receivers. (FIFO queue of the receivers).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They need to be closed manually once we are done sending data or when an exception occurs: myChannel.close(). Otherwise the receivers will wait for elements forever.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Not lifecycle-aware. Acts like a queue (FIFO).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When to Use",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For one-time events like navigation or snackbars",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In coroutine-based data pipelines",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "emit()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The emit function is a suspending function, which means it can suspend the current coroutine until the data is successfully sent through the channel. When you call emit, the coroutine is suspended until the receiver consumes the data or the channel is closed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/XDdevv/PLJuneMiniChallenges.git
            KotlinCode(
                code = """
                    @Composable
                    fun <T> ObserveAsEvents(
                        flow: Flow<T>,
                        key1: Any? = null,
                        key2: Any? = null,
                        onEvent: (T) -> Unit
                    ) {
                        val lifecycleOwner = LocalLifecycleOwner.current
                        LaunchedEffect(lifecycleOwner.lifecycle, key1, key2) {
                            lifecycleOwner.repeatOnLifecycle(Lifecycle.State.STARTED) {
                                withContext(Dispatchers.Main.immediate) {
                                    flow.collect(onEvent)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    fun main() = runBlocking {
                        val channel = Channel<Int>()

                        launch {
                            for (i in 1..5) {
                                channel.send(i)  // Equivalent to emit, suspends until data is sent
                                println("Sent: $i")
                            }
                            channel.close()  // Close the channel once done
                        }

                        for (msg in channel) {
                            println("Received: $msg")
                        }
                    }
                    Pros of emit:
                    Suspension Guarantees…

                    """.trimIndent()
            )

            SpanText(
                text = "Can I use emit() inside these builders?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use send() (suspending) or trySend() (non-blocking). send is the idiomatic way to communicate with the underlying channel that powers these flows.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What exactly happens if I forget awaitClose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The code block registers the listener and immediately reaches the end of the block. The internal Channel closes, telling the collector the flow is finished. However, the listener remains registered in the system — wasting memory and CPU.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Creating a Channel that will close automatically",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //1. The coroutine builder, produce closes the channel whenever the builder coroutine ends (it finishes, stopes or is cancelled).
                    suspend fun myFunction() = coroutineScope {
                      val channel = produce {
                        // emit values here and don't need to call close() at the end
                      }
                    }
                    
                    //2. If the channel was closed or cancelled or when send, receive or hastNext throw an error
                    val myChannel = Channel(
                      capacity,
                      onUnderliveredElement = { /* clean up operations here */ }
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Channel capacity types",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                KotlinCode(
                    code = """
                    val myChannel = Channel<Int>(capacity = 3)

                    // OR

                    val myChannel = produce(capacity = 3) {
                      // emit values here
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channel.UNLIMITED: unlimited buffer and send never suspends.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channel.BUFFERED: Buffer capacity of 64. This default value can be overridden with the system property kotlinx.coroutines.channels.defaultBuffer in JVM.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channel.RENDEZVOUS: (default behavior) buffer capacity of 0. The receiver will only receive the data if it is subscribed to the sender when the data is being emitted.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channel.CONFLATED: Useful when collectors only need the most recent state. Buffer capacity of 1. Each new element replaces the previous one. (Only the latest value is retained in the channel, making it efficient when only the most recent update matters)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Any int value: Buffer will have the capacity set by the int.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Handling buffer overflow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "BufferOverflow.SUSPEND: (default behavior) suspend the send method when the buffer is full.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "BufferOverflow.DROP_OLDEST: drop the oldest element when the buffer is full.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "BufferOverflow.DROP_LATEST: drop the latest element when the buffer is full.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Producer-Consumer Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Channels enable one coroutine (the producer) to send data to another coroutine (the consumer) without the need for shared mutable state or explicit synchronization.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.channels.*

                    fun main() = runBlocking {
                        val channel = Channel<Int>()

                        val producer = launch {
                            repeat(5) {
                                delay(100) // Simulate some work
                                channel.send(it)
                            }
                            channel.close()
                        }

                        val consumer = launch {
                            for (item in channel) {
                                println(item)
                            }
                        }

                        producer.join()
                        consumer.join()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Key points regarding coroutine channels",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Asynchronous Data Transfer: Data can be asynchronously sent and received through channels, allowing coroutines to communicate without blocking, which is crucial for efficient concurrency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Buffering: Channels can be configured with a buffer size, which can help in managing the flow of data when the producer and consumer operate at different speeds.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Backpressure Handling: Channels can be used to handle backpressure scenarios, where the producer may need to slow down if the consumer can’t keep up with the incoming data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Flow Control: Channels offer fine-grained control over how data is transmitted between coroutines, allowing for complex data processing and transformation scenarios.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Hot and Cold Channels: Channels can be used in both hot (producing data immediately) and cold (producing data when requested) scenarios, offering flexibility based on your needs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "1 - Network Observer",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import android.content.Context
                    import android.net.ConnectivityManager
                    import android.net.Network
                    import kotlinx.coroutines.channels.awaitClose
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.callbackFlow
                    import kotlinx.coroutines.flow.distinctUntilChanged
                    import kotlinx.coroutines.launch

                    class NetworkObserver(context: Context) {

                        enum class Status {
                            Available, Unavailable, Loosing, Lost
                        }

                        private val connectivityManager =
                            context.getSystemService(Context.CONNECTIVITY_SERVICE) as ConnectivityManager

                        fun observe(): Flow<Status> {
                            return callbackFlow {
                                val callback = object : ConnectivityManager.NetworkCallback() {

                                    override fun onAvailable(network: Network) {
                                        super.onAvailable(network)
                                        launch { send(Status.Available) }
                                    }

                                    override fun onLosing(network: Network, maxMsToLive: Int) {
                                        super.onLosing(network, maxMsToLive)
                                        launch { send(Status.Loosing) }

                                    }

                                    override fun onLost(network: Network) {
                                        super.onLost(network)
                                        launch { send(Status.Lost) }
                                    }

                                    override fun onUnavailable() {
                                        super.onUnavailable()
                                        launch { send(Status.Unavailable) }
                                    }
                                }

                                connectivityManager.registerDefaultNetworkCallback(callback)
                                awaitClose {
                                    connectivityManager.unregisterNetworkCallback(callback)
                                }

                            }.distinctUntilChanged()
                        }
                    }
                    
                    //UI
                    private lateinit var networkObserver: NetworkObserver
                    networkObserver = NetworkObserver(applicationContext)
                    """.trimIndent()
            )

            SpanText(
                text = "2 - Network Observer",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import android.content.Context
                    import android.net.ConnectivityManager
                    import android.net.Network
                    import android.net.NetworkCapabilities
                    import android.net.NetworkRequest
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.State
                    import androidx.compose.runtime.produceState
                    import androidx.compose.ui.platform.LocalContext
                    import kotlinx.coroutines.ExperimentalCoroutinesApi
                    import kotlinx.coroutines.channels.awaitClose
                    import kotlinx.coroutines.flow.callbackFlow

                    sealed class ConnectionState {
                        object Available : ConnectionState()
                        object Unavailable : ConnectionState()
                    }

                    /**
                     * Network utility to get current state of internet connection
                     */
                    val Context.currentConnectivityState: ConnectionState
                        get() {
                            val connectivityManager =
                                getSystemService(Context.CONNECTIVITY_SERVICE) as ConnectivityManager
                            return getCurrentConnectivityState(connectivityManager)
                        }

                    @ExperimentalCoroutinesApi
                    @Composable
                    fun connectivityState(): State<ConnectionState> {
                        val context = LocalContext.current

                        // Creates a State<ConnectionState> with current connectivity state as initial value
                        return produceState(initialValue = context.currentConnectivityState) {
                            // In a coroutine, can make suspend calls
                            observeConnectivityAsFlow(context).collect { value = it }
                        }
                    }

                    private fun getCurrentConnectivityState(
                        connectivityManager: ConnectivityManager
                    ): ConnectionState {
                        val connected = connectivityManager.allNetworks.any { network ->
                            connectivityManager.getNetworkCapabilities(network)
                                ?.hasCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET)
                                ?: false
                        }

                        return if (connected) ConnectionState.Available else ConnectionState.Unavailable
                    }

                    /**
                     * Network Utility to observe availability or unavailability of Internet connection
                     */
                    @ExperimentalCoroutinesApi
                    private fun observeConnectivityAsFlow(context: Context) = callbackFlow {
                        val connectivityManager = context.getSystemService(Context.CONNECTIVITY_SERVICE) as ConnectivityManager

                        val callback = networkCallback { connectionState -> trySend(connectionState) }

                        val networkRequest = NetworkRequest.Builder()
                            .addCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET)
                            .build()

                        connectivityManager.registerNetworkCallback(networkRequest, callback)

                        // Set current state
                        val currentState = getCurrentConnectivityState(connectivityManager)
                        trySend(currentState)

                        // Remove callback when not used
                        awaitClose {
                            // Remove listeners
                            connectivityManager.unregisterNetworkCallback(callback)
                        }
                    }

                    private fun networkCallback(callback: (ConnectionState) -> Unit): ConnectivityManager.NetworkCallback {
                        return object : ConnectivityManager.NetworkCallback() {
                            override fun onAvailable(network: Network) {
                                callback(ConnectionState.Available)
                            }

                            override fun onLost(network: Network) {
                                callback(ConnectionState.Unavailable)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class NetworkConnection(context: Context) {
                        private val connectivityManager = context.getSystemService(Context.CONNECTIVITY_SERVICE) as ConnectivityManager
                        private val _networkStatus = MutableStateFlow(false)
                        val networkStatus: StateFlow<Boolean> = _networkStatus

                        init {
                            observeNetworkStatus()
                        }

                        private fun observeNetworkStatus() {
                            val callback = object : ConnectivityManager.NetworkCallback() {
                                override fun onAvailable(network: Network) {
                                    val networkCapabilities = connectivityManager.getNetworkCapabilities(network)
                                    val hasInternet =
                                        networkCapabilities?.hasCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET) == true
                                    val isActive = connectivityManager.activeNetwork == network
                                    _networkStatus.value = hasInternet && isActive
                                }

                                override fun onLost(network: Network) {
                                    Handler(Looper.getMainLooper()).postDelayed({
                                        val networkCapabilities =
                                            connectivityManager.getNetworkCapabilities(connectivityManager.activeNetwork)
                                        _networkStatus.value =
                                            networkCapabilities?.hasTransport(NetworkCapabilities.TRANSPORT_WIFI) == true || networkCapabilities?.hasTransport(
                                                NetworkCapabilities.TRANSPORT_CELLULAR
                                            ) == true
                                    },500)
                                }
                            }

                            val networkRequest = NetworkRequest.Builder()
                                .addCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET)
                                .build()

                            connectivityManager.requestNetwork(networkRequest, callback)
                        }
                    }
                    
                    //UI
                    private val networkConnection: NetworkConnection by inject()
                    
                    val networkStatus = networkConnection.networkStatus.map(Boolean::not).stateIn(
                       scope = rememberCoroutineScope(),
                       started = SharingStarted.WhileSubscribed(5_000),
                       initialValue = false
                
                    LaunchedEffect(key1 = Unit) {
                        networkStatus.collect { isOffline ->
                            
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Channel that will close automatically",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    suspend fun myFunction() = coroutineScope {
                      val channel = produce {
                        // emit values here and don't need to call close() at the end
                      }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Automatically clean up if an element could not be handled",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val myChannel = Channel(
                      capacity,
                      onUnderliveredElement = { /* clean up operations here */ }
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // This is a simplify version to demonstrate how we can use channels. 
                    // In a real use case, we would require some extra logic to avoid 
                    // refreshing if the data is already loading for example.

                    interface ApiService {
                        suspend fun fetchData(): List<String>
                    }

                    class FetchDataUseCase @Inject constructor (
                      private val apiService: ApiService
                    ) {
                      // create a channel with a buffer of 1 and will drop the newest data
                      // so if we trigger refresh several times in a row we will only
                      // keep the first element.
                      private val refreshChannel = Channel<Unit>(
                            capacity = 1, 
                            onBufferOverflow = BufferOverflow.DROP_LATEST
                        )

                      // this flow can be received by the viewModel to build the UI state
                      val dataState: Flow<FetchDataState> =
                            refreshChannel
                                // convert channel into a flow
                                .consumeAsFlow()
                                // emit an element on start to fetch data as soon as we subscribe 
                                // to the flow
                                .onStart { emit(Unit) }
                                .map { fetchData() }

                      fun refresh() {
                        // We use the trySend function here to not have to create a 
                        // suspend function and so we don't need a scope to call it.
                        // this method can be called from the viewModel to trigger a refresh
                        refreshChannel.trySend(Unit)
                      }

                      private suspend fun fetchData(): FetchDataState =
                        try {
                            val data = apiService.fetchData()
                            FetchDataState.Success(data)
                        } catch (e: Exception) {
                            FetchDataState.Error(e.message ?: "An error occurred")
                        }

                       sealed interface FetchDataState {
                            data object Loading : FetchDataState
                            data class Success(val data: List<String>) : FetchDataState
                            data class Error(val message: String) : FetchDataState
                       }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    import kotlinx.coroutines.channels.Channel
                    import kotlinx.coroutines.flow.collect
                    import kotlinx.coroutines.flow.onEach
                    import kotlinx.coroutines.flow.receiveAsFlow
                    import timber.log.Timber

                    // Used for communicating single event effects to a single consumer.
                    // Should *NOT* be used for things that *must* be processed, as there is always a possibility of a hot flow emission not being collected.
                    class SingleEventFlow<T> {

                        private val channel = Channel<T>(capacity = 1)

                        fun send(event: T) {
                            val prev = channel.tryReceive().getOrNull()
                            if (prev != null) {
                                Timber.e("Event $prev was not collected before a new one $ event was sent")
                            }
                            val sent = channel.trySend(event)
                            if (sent.isFailure) {
                                Timber.e("Channel send failure: ${sent.exceptionOrNull()?.message}")
                            }
                        }

                        suspend fun consumeEvents(handleEvent: (T) -> Unit) {
                            channel.receiveAsFlow().onEach {
                                handleEvent(it)
                            }.collect()
                        }
                    }
                    """.trimIndent()
            )

            /*SpanText(
                text = "",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }*/
        }
    }
}

